
import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { 
  MessageSquare, 
  PhoneOff, 
  ChevronRight,
  Volume2,
  VolumeX
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export interface UserControlsProps {
  onNext: () => void;
  onEnd: () => void;
  onToggleTextChat: () => void;
  onToggleAudio?: () => void;
  isTextChatActive?: boolean;
  isAudioEnabled?: boolean;
  position?: 'left-center' | 'bottom-center';
  isVisible?: boolean;
  customControls?: Array<{
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    active?: boolean;
  }>;
}

const UserControls: React.FC<UserControlsProps> = ({ 
  onNext, 
  onEnd, 
  onToggleTextChat,
  onToggleAudio,
  isTextChatActive = false,
  isAudioEnabled = true,
  position = 'left-center',
  isVisible = true,
  customControls = []
}) => {
  const isVertical = position === 'left-center';
  const { t } = useLanguage();
  
  // Controls in the specific order: chatting, next partner, end call
  const controlsOrder = [
    {
      id: 'chat',
      element: (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onToggleTextChat}
          className={cn(
            "p-3 rounded-full flex items-center justify-center",
            isTextChatActive
              ? "bg-[#292D67] text-white"
              : "bg-white/10 text-white hover:bg-[#292D67]/30",
            isVertical && "hover:bg-[#292D67]/30"
          )}
          aria-label={t('chat.toggleChat')}
          title={t('chat.toggleChat')}
        >
          <MessageSquare className="h-5 w-5" />
        </motion.button>
      )
    },
    {
      id: 'next',
      element: (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onNext}
          className={cn(
            "p-3 rounded-full flex items-center justify-center bg-[#4A6FA5] text-white hover:bg-[#385A8A]",
            isVertical && "hover:bg-[#385A8A]"
          )}
          aria-label={t('chat.findNext')}
          title={t('chat.findNext')}
        >
          <ChevronRight className="h-5 w-5" />
        </motion.button>
      )
    },
    {
      id: 'end',
      element: (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onEnd}
          className={cn(
            "p-3 rounded-full flex items-center justify-center bg-[#E74C3C] text-white hover:bg-[#C0392B]",
            isVertical && "hover:bg-[#C0392B]"
          )}
          aria-label={t('chat.endChat')}
          title={t('chat.endChat')}
        >
          <PhoneOff className="h-5 w-5" />
        </motion.button>
      )
    }
  ];
  
  // Add sound toggle button if the callback is provided
  if (onToggleAudio) {
    controlsOrder.push({
      id: 'audio',
      element: (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onToggleAudio}
          className={cn(
            "p-3 rounded-full flex items-center justify-center",
            isAudioEnabled
              ? "bg-white/10 text-white hover:bg-[#292D67]/30"
              : "bg-[#292D67]/50 text-white",
            isVertical && "hover:bg-[#292D67]/30"
          )}
          aria-label={isAudioEnabled ? t('chat.muteAudio') : t('chat.unmuteAudio')}
          title={isAudioEnabled ? t('chat.muteAudio') : t('chat.unmuteAudio')}
        >
          {isAudioEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
        </motion.button>
      )
    });
  }
  
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ delay: isVisible ? 0 : 0.5, duration: 0.3 }}
      className={cn(
        "fixed z-40 flex gap-3",
        isVisible ? "pointer-events-auto" : "pointer-events-none",
        isVertical 
          ? "left-4 top-1/2 -translate-y-1/2 flex-col p-3" 
          : "bottom-4 left-1/2 -translate-x-1/2 items-center p-3 rounded-full bg-[#0a0e1e]/80 backdrop-blur-sm border border-[#292D67]/20"
      )}
    >
      {controlsOrder.map(control => control.element)}
      {customControls.map((control, index) => (
        <motion.button
          key={`custom-${index}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={control.onClick}
          className={cn(
            "p-3 rounded-full flex items-center justify-center",
            control.active
              ? "bg-[#292D67] text-white"
              : "bg-white/10 text-white hover:bg-[#292D67]/30"
          )}
          aria-label={control.label}
          title={control.label}
        >
          {control.icon}
        </motion.button>
      ))}
    </motion.div>
  );
};

export default UserControls;
